// eslint-disable-next-line import/no-extraneous-dependencies
import { BehaviorSubject } from 'rxjs';
import { map } from 'rxjs/operators';

import dayjs from 'dayjs';

import { withDefaults } from 'Utilities/mappers';
import { isDealerIdRestricted } from 'Utilities/restrictedDealerIds';

import { APIResponseStatus } from 'Services/api.types';

import { PreferenceStatus, SellerCollectionPreference } from '../documentCapture/DealerDetails/CollectionPreferences/collectionPreferences.types';
import { UseGetPreferencesStatuses } from '../documentCapture/DealerDetails/hooks/useGetPreferencesStatuses';

import vehicleImagePlaceHolder from './assets/vehicleImagePlaceholder.svg';
import { isAfterCollectionDeadline, isPastDate, isToday } from './utils/datetime';
import { PaymentDetailsState, SaleDetailsState, VehicleCollectionState } from './VehicleCollectionSubjects.types';

export const defaultVehicleCollection: VehicleCollectionState = {
  collectionPreferences: {
    datePreferencesState: undefined,
    expiryAt: undefined,
    loading: true,
    refetch: () => {},
    status: PreferenceStatus.NOT_REQUESTED,
  },
  displayName: 'VehicleCollectionContext',
  enquiryId: undefined,
  isPostSaleDataLoading: true,
  offer: undefined,
  offerStatus: undefined,
  saleDetails: { status: undefined },
  vehicle: undefined,
  vrm: '',
};

export const defaultSaleDetailsState: SaleDetailsState = {
  accountNumber: '',
  dealershipName: '',
  dealershipPhone: '',
  email: '',
  enquiryId: null,
  financingAmount: null,
  hasNegativeEquity: false,
  make: '',
  model: '',
  neBankTransferTotalAmount: null,
  offerStatus: APIResponseStatus.LOADING,
  refetchOffer: () => {},
  sellerAmount: null,
  serviceFee: null,
  serviceFeeTiers: null,
  soldForFigure: null,
  vehicleProfilePath: '',
  vehicleThumbnail: '',
  vrm: '',
};

export const defaultPaymentDetailsState: PaymentDetailsState = {
  hasPendingApproval: false,
  isCounterOffer: false,
  offerAmount: 0,
  paymentPageStatus: 'entry',
  reference: undefined,
  shouldRedirectToVehiclePaymentPage: false,
};

export const vehicleCollectionState$ = new BehaviorSubject<VehicleCollectionState>(defaultVehicleCollection);

export const vehicleCollectionStore = {
  setIsSaleSupportModalOpen: (isSaleSupportModalOpen: boolean) => {
    const currentState = vehicleCollectionState$.value;
    vehicleCollectionState$.next({
      ...currentState,
      isSaleSupportModalOpen,
    });
  },
  updateCollectedAt: (collectedAt: string) => {
    const currentState = vehicleCollectionState$.value;
    vehicleCollectionState$.next({
      ...currentState,
      collectionDetails: {
        ...currentState.collectionDetails,
        data: {
          ...currentState?.collectionDetails?.data,
          collection: {
            ...currentState?.collectionDetails?.data?.collection,
            collectedAt,
          },
        },
      },
    });
  },
  updateCollectionPreferencesDates: (collectionPreferences: SellerCollectionPreference['datePreferences']) => {
    const currentState = vehicleCollectionState$.value;
    vehicleCollectionState$.next({
      ...currentState,
      collectionPreferences: {
        ...currentState.collectionPreferences,
        datePreferencesState: {
          ...currentState?.collectionPreferences?.datePreferencesState,
          datePreferences: collectionPreferences,
        },
      } as UseGetPreferencesStatuses,
    });
  },
};

export const resolveSellerAmountDetails = (state: VehicleCollectionState): {
  acceptedBid: number;
  hasNegativeEquity: boolean;
  neBankTransferTotalAmount: Nilable<number>;
  sellerAmount: number;
  settlementFigure: number;
} => {
  const postSaleOfferAmount = state.latestPostSaleOfferData?.amount;
  const postSaleSellerAmount = state.latestPostSaleOfferData?.priceBreakdown?.find?.((p) => p.type === 'seller_amount')?.amount;
  const postSaleFinanceAmount = state.latestPostSaleOfferData?.priceBreakdown?.find?.((p) => p.type === 'financing_amount')?.amount;

  const marketplaceAmount = Number(state?.offer?.acceptedBid?.value) || 0;
  const marketplaceFinanceFigure = Number(state?.offer?.data?.finance?.settlementFigure) || 0;

  const getAcceptedBidValue = () => {
    // Data from post sale offer has highest priority
    if (typeof postSaleOfferAmount === 'number') {
      return postSaleOfferAmount;
    }

    // otherwise, fallback to marketplace offer
    return marketplaceAmount;
  };

  const getHasNegativeEquity = () => {
    // Data from finance status has highest priority
    if (typeof state.financeStatusData?.isNegativeEquity === 'boolean') {
      return state.financeStatusData!.isNegativeEquity;
    }

    // then we rely on post sale data
    if (typeof postSaleSellerAmount === 'number') {
      return postSaleSellerAmount < 0;
    }

    // lastly, fallback to marketplace data
    return marketplaceFinanceFigure > marketplaceAmount;
  };

  const getSellerAmount = () => {
    // Data from finance status has highest priority
    if (state.financeStatusData?.isNegativeEquity) {
      const { amountOwed, amountRemaining, status } = state.financeStatusData.financeStatus.seller;

      if (status !== 'unpaid') {
        if (typeof amountRemaining === 'number') {
          return Math.max(0, amountRemaining);
        }
      }

      if (typeof amountOwed === 'number') {
        return amountOwed;
      }
    }

    // then we rely on post sale data
    if (typeof postSaleSellerAmount === 'number') {
      return Math.abs(postSaleSellerAmount);
    }

    // lastly, fallback to marketplace data
    return Math.abs(marketplaceAmount - marketplaceFinanceFigure);
  };

  const getSettlementFigure = () => {
    // Data from post sale offer has highest priority
    if (typeof postSaleFinanceAmount === 'number') {
      return postSaleFinanceAmount;
    }

    // otherwise, fallback to marketplace offer
    return marketplaceFinanceFigure;
  };

  const getNEBankTransferTotalAmount = () => {
    // finance status is the only source of bank transfers
    if (state.financeStatusData?.isNegativeEquity) {
      const { amountPaid } = state.financeStatusData.financeStatus.seller;

      if (typeof amountPaid === 'number') {
        return amountPaid;
      }
    }

    return null;
  };

  return {
    acceptedBid: getAcceptedBidValue(),
    hasNegativeEquity: getHasNegativeEquity(),
    neBankTransferTotalAmount: getNEBankTransferTotalAmount(),
    sellerAmount: getSellerAmount(),
    settlementFigure: getSettlementFigure(),
  };
};

export const stateToSaleDetailsViewModelMapper = (state: VehicleCollectionState): SaleDetailsState => {
  const {
    acceptedBid, hasNegativeEquity, neBankTransferTotalAmount, sellerAmount, settlementFigure,
  } = resolveSellerAmountDetails(state);

  const vehicleProfileImage = state?.offer?.vehicleImages?.filter((image) => image?.kind === 'exterior_front_driver')?.[0]?.presets?.[0]?.url;

  const dealerId = state?.offer?.closingBid?.dealer?.id ?? state?.offer?.acceptedBid?.dealerContact?.dealer?.id;
  const isRestricted = isDealerIdRestricted(dealerId);

  return withDefaults(defaultSaleDetailsState, {
    accountNumber: state?.beneficiary?.accountNumber,
    dealershipName: state?.offer?.acceptedBid?.dealer?.name,
    dealershipPhone: isRestricted
      ? ''
      : state?.offer?.acceptedBid?.dealer?.contactPhone || state?.offer?.acceptedBid?.dealer?.companyPhone,
    email: isRestricted ? '' : state?.offer?.acceptedBid?.dealer?.email,
    enquiryId: state?.enquiryId ?? null,
    financingAmount: settlementFigure,
    hasNegativeEquity,
    make: state?.vehicle?.make?.display_name,
    model: state?.vehicle?.model,
    neBankTransferTotalAmount,
    offerStatus: state?.offerStatus,
    refetchOffer: state?.refetchOffer,
    sellerAmount,
    serviceFee: state?.serviceFee ?? null,
    serviceFeeTiers: state?.serviceFeeTiers ?? null,
    soldForFigure: acceptedBid,
    vehicleProfilePath: state?.vehicleProfilePath,
    vehicleThumbnail: vehicleProfileImage ?? vehicleImagePlaceHolder,
    vrm: state?.vrm,
  });
};

export const stateToNegativeEquityModelMapper = (state: VehicleCollectionState) => {
  const { hasNegativeEquity, sellerAmount } = resolveSellerAmountDetails(state);

  return hasNegativeEquity ? sellerAmount : 0;
};

const resolvePaymentPageStatus = (state: VehicleCollectionState): PaymentDetailsState['paymentPageStatus'] => {
  const paymentStatus = state.paymentsStatusesData?.status || 'initial';
  const postSaleOfferState = state.latestPostSaleOfferData?.state || 'pending';
  const isApprovedBySeller = Boolean((state.latestPostSaleOfferData?.approvals || []).find(({ userType }) => userType === 'seller'));

  switch (postSaleOfferState) {
  case 'rejected':
    return 'error';
  case 'pending':
    return isApprovedBySeller ? 'pending' : 'entry';
  case 'approved':
    switch (paymentStatus) {
    case 'done':
    // case 'not_required':
      return 'success';
    case 'failed':
    case 'errored':
      return 'error';
    case 'initial':
    case 'pending':
    default:
      return 'pending';
    }
  default:
    return 'entry';
  }
};

export const stateToPaymentDetailsModelMapper = (state: VehicleCollectionState): PaymentDetailsState => {
  const { hasNegativeEquity } = resolveSellerAmountDetails(state);
  const paymentPageStatus = resolvePaymentPageStatus(state);

  const isCounterOffer = state.latestPostSaleOfferData?.type === 'counter_offer';

  const hasPendingApproval = Boolean(
    isCounterOffer
    && !state.latestPostSaleOfferData?.approvals?.find?.(({ userType }) => userType === 'seller'),
  );

  const shouldRedirectToVehiclePaymentPage = Boolean(
    isCounterOffer
    && (hasNegativeEquity ? paymentPageStatus === 'entry' : paymentPageStatus !== 'success'),
  );

  return withDefaults(defaultPaymentDetailsState, {
    hasPendingApproval,
    isCounterOffer,
    offerAmount: state.latestPostSaleOfferData?.amount,
    paymentPageStatus: resolvePaymentPageStatus(state),
    reference: state.paymentsStatusesData?.reference,
    shouldRedirectToVehiclePaymentPage,
  });
};

export const stateToPaymentMVPMapper = (
  state: VehicleCollectionState,
): boolean => Boolean(state?.offer?.features?.paymentsMVP);

export const saleDetailsViewModel$ = vehicleCollectionState$.pipe(
  map(stateToSaleDetailsViewModelMapper),
);

export const negativeEquityValue$ = vehicleCollectionState$.pipe(
  map(stateToNegativeEquityModelMapper),
);

export const isPayDealer$ = vehicleCollectionState$.pipe(
  map(stateToPaymentMVPMapper),
);

export const paymentDetailsModel$ = vehicleCollectionState$.pipe(
  map(stateToPaymentDetailsModelMapper),
);

export type CollectionDetailsStatus =
  typeof COLLECTION_DETAILS_STATUSES_MAP[keyof typeof COLLECTION_DETAILS_STATUSES_MAP];

export const COLLECTION_DETAILS_STATUSES_MAP = {
  COLLECTION_COMPLETED: 'COLLECTION_COMPLETED',
  COLLECTION_DAY: 'COLLECTION_DAY',
  COLLECTION_PREFERENCES_COMPLETED: 'COLLECTION_PREFERENCES_COMPLETED',
  COLLECTION_PREFERENCES_NOT_REQUESTED: 'COLLECTION_PREFERENCES_NOT_REQUESTED',
  COLLECTION_PREFERENCES_REQUESTED: 'COLLECTION_PREFERENCES_REQUESTED',
  COLLECTION_RESCHEDULED: 'COLLECTION_RESCHEDULED',
  COLLECTION_UNKNOWN: 'COLLECTION_UNKNOWN',
  INSPECTION_COMPLETED: 'INSPECTION_COMPLETED',
  INSPECTION_STARTED: 'INSPECTION_STARTED',
  SCHEDULED_COLLECTION_DATE_CONFIRMED: 'SCHEDULED_COLLECTION_DATE_CONFIRMED',
} as const;

export const COLLECTION_DEADLINE_CONFIG = {
  DEADLINE_HOUR: 17,
  DEADLINE_MINUTE: 0,
  DEADLINE_SECOND: 0,
} as const;

export const getCurrentCollectionStatus = (state: VehicleCollectionState) => {
  const {
    COLLECTION_COMPLETED,
    COLLECTION_DAY,
    COLLECTION_PREFERENCES_COMPLETED,
    COLLECTION_PREFERENCES_NOT_REQUESTED,
    COLLECTION_PREFERENCES_REQUESTED,
    COLLECTION_RESCHEDULED,
    COLLECTION_UNKNOWN,
    INSPECTION_COMPLETED,
    INSPECTION_STARTED,
    SCHEDULED_COLLECTION_DATE_CONFIRMED,
  } = COLLECTION_DETAILS_STATUSES_MAP;

  const { DEADLINE_HOUR, DEADLINE_MINUTE, DEADLINE_SECOND } = COLLECTION_DEADLINE_CONFIG;

  const {
    collectedAt,
    inspection,
    rescheduled,
    scheduledCollectionDate,
  } = state?.collectionDetails?.data?.collection ?? {};

  const { expiryAt: preferencesExpiryAt = '', status: collectionPreferencesStatus } = state?.collectionPreferences ?? {};
  const { completedAt: inspectionCompletedAt, startedAt: inspectionStartedAt } = inspection ?? {};

  const collectionDateDeadline = scheduledCollectionDate
    ? dayjs(scheduledCollectionDate).hour(DEADLINE_HOUR).minute(DEADLINE_MINUTE).second(DEADLINE_SECOND) : undefined;

  const isCollectionRescheduled = (!scheduledCollectionDate && rescheduled)
    || (scheduledCollectionDate && isAfterCollectionDeadline(collectionDateDeadline));

  // the order of the checks is important
  if (collectedAt && isPastDate(collectedAt)) {
    return COLLECTION_COMPLETED;
  }

  if (inspectionCompletedAt) {
    return INSPECTION_COMPLETED;
  }

  if (inspectionStartedAt) {
    return INSPECTION_STARTED;
  }

  if (isCollectionRescheduled) {
    return COLLECTION_RESCHEDULED;
  }

  if (scheduledCollectionDate && isToday(scheduledCollectionDate)) {
    return COLLECTION_DAY;
  }

  if (scheduledCollectionDate) {
    return SCHEDULED_COLLECTION_DATE_CONFIRMED;
  }

  if (collectionPreferencesStatus === PreferenceStatus.COMPLETED || isPastDate(preferencesExpiryAt)) {
    return COLLECTION_PREFERENCES_COMPLETED;
  }

  if (collectionPreferencesStatus === PreferenceStatus.REQUESTED) {
    return COLLECTION_PREFERENCES_REQUESTED;
  }

  if (collectionPreferencesStatus === PreferenceStatus.NOT_REQUESTED) {
    return COLLECTION_PREFERENCES_NOT_REQUESTED;
  }

  return COLLECTION_UNKNOWN;
};

export const currentVehicleCollectionStatus$ = vehicleCollectionState$.pipe(
  map(getCurrentCollectionStatus),
);
